import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Card, CardTitle, CardDescription, Badge } from '@/components/ui'
import { useAppHistory, type ValuationHistoryEntry } from '@/hooks/useAppHistory'
import { useAppImprovements } from '@/hooks/useImprovements'
import {
  History, FileText, Film, FileCode2, TrendingUp, ExternalLink,
  Clock, ArrowUpRight, ArrowDownRight, Network,
} from 'lucide-react'
import { cn, formatCurrency, formatDateLong, formatRelativeDate } from '@/lib/utils'

type Tab = 'all' | 'valuations' | 'docs' | 'gamma' | 'audits' | 'synergies'

export function AppHistoryPanel({ appSlug }: { appSlug: string }) {
  const h = useAppHistory(appSlug)
  const { items: improvements } = useAppImprovements(appSlug)
  const [tab, setTab] = useState<Tab>('all')

  // Group improvements by iteration (audit cycles) so the timeline view shows
  // one entry per audit, with a count of items + completion ratio.
  const auditCycles = (() => {
    const map = new Map<number, { iteration: number; generated_at: string; total: number; done: number; high: number }>()
    for (const it of improvements) {
      const k = it.generated_iteration
      const cur = map.get(k) ?? { iteration: k, generated_at: it.generated_at, total: 0, done: 0, high: 0 }
      cur.total += 1
      if (it.status === 'done') cur.done += 1
      if (it.impact === 'high') cur.high += 1
      if (it.generated_at > cur.generated_at) cur.generated_at = it.generated_at
      map.set(k, cur)
    }
    return Array.from(map.values()).sort((a, b) => b.iteration - a.iteration)
  })()

  const counts = {
    valuations: h.valuations.length,
    docs: h.valuationDocs.length,
    gamma: h.gammaArtifacts.length,
    audits: auditCycles.length,
    synergies: h.synergies.length,
    total: h.valuations.length + h.valuationDocs.length + h.gammaArtifacts.length + auditCycles.length + h.synergies.length,
  }

  // How many records the currently selected tab has — drives a tab-specific
  // empty state so selecting e.g. "Docs" with zero records doesn't render blank.
  const activeTabCount = tab === 'all' ? counts.total : counts[tab]

  return (
    <Card className="space-y-5">
      <div>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="eyebrow mb-1 flex items-center gap-1.5">
              <History size={11} /> Asset History
            </div>
            <CardTitle>Every record we've kept on this app</CardTitle>
            <CardDescription className="mt-1">
              Append-only · nothing is overwritten · compare growth over time. Newest first.
            </CardDescription>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-cream/55">
            {counts.total} records
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 flex-wrap border-b border-bronze/15 pb-3">
        <TabBtn label={`All ${counts.total}`}        active={tab === 'all'}        on={() => setTab('all')} />
        <TabBtn label={`Valuations ${counts.valuations}`} active={tab === 'valuations'} on={() => setTab('valuations')} />
        <TabBtn label={`Docs ${counts.docs}`}        active={tab === 'docs'}       on={() => setTab('docs')} />
        <TabBtn label={`Gamma ${counts.gamma}`}      active={tab === 'gamma'}      on={() => setTab('gamma')} />
        <TabBtn label={`Audits ${counts.audits}`}    active={tab === 'audits'}     on={() => setTab('audits')} />
        <TabBtn label={`Synergies ${counts.synergies}`} active={tab === 'synergies'} on={() => setTab('synergies')} />
      </div>

      {h.loading ? (
        <div className="text-cream/55 text-sm">Loading history…</div>
      ) : counts.total === 0 ? (
        <div className="text-center py-8 border border-dashed border-bronze/30 rounded-lg">
          <Clock size={28} className="text-bronze/60 mx-auto mb-3" />
          <p className="font-body text-cream/65 text-sm max-w-md mx-auto">
            Nothing in history yet. Run a valuation, generate a doc, or audit this app to start the record.
          </p>
        </div>
      ) : activeTabCount === 0 ? (
        <div className="text-center py-8 border border-dashed border-bronze/30 rounded-lg">
          <Clock size={28} className="text-bronze/60 mx-auto mb-3" />
          <p className="font-body text-cream/65 text-sm max-w-md mx-auto">
            No records in this tab yet. Switch to <button onClick={() => setTab('all')} className="text-gold-soft hover:underline">All</button> to see everything.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {(tab === 'all' || tab === 'valuations') && h.valuations.length > 0 && (
            <Section title="Valuation snapshots" subtitle="Each row is a Gemini run — stored in full so you can compare midpoints and rationale across runs.">
              <div className="space-y-2">
                {h.valuations.map((v, i) => (
                  <ValuationRow key={v.id} v={v} prev={h.valuations[i + 1]} />
                ))}
              </div>
            </Section>
          )}

          {(tab === 'all' || tab === 'docs') && h.valuationDocs.length > 0 && (
            <Section title="Branded Google Docs" subtitle="Every render preserved — older versions stay accessible for buyer-side comparison.">
              <div className="space-y-2">
                {h.valuationDocs.map(d => (
                  <a key={d.id} href={d.google_doc_url} target="_blank" rel="noreferrer"
                     className="flex items-center gap-3 px-3 py-2 rounded border border-cream/8 bg-navy/30 hover:bg-navy/60 hover:border-gold/30 transition-colors">
                    <FileText size={14} className="text-gold-soft shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-body text-sm text-cream truncate">Valuation Memorandum</div>
                      <div className="font-mono text-[10px] text-cream/45">
                        {formatDateLong(d.generated_at)} · {formatRelativeDate(d.generated_at)}
                      </div>
                    </div>
                    <ExternalLink size={11} className="text-bronze shrink-0" />
                  </a>
                ))}
              </div>
            </Section>
          )}

          {(tab === 'all' || tab === 'gamma') && h.gammaArtifacts.length > 0 && (
            <Section title="Gamma artifacts" subtitle="Whitepapers and marketing decks rendered through Gamma. Older versions remain live.">
              <div className="space-y-2">
                {h.gammaArtifacts.map(g => (
                  <a key={g.id} href={g.url} target="_blank" rel="noreferrer"
                     className="flex items-center gap-3 px-3 py-2 rounded border border-cream/8 bg-navy/30 hover:bg-navy/60 hover:border-gold/30 transition-colors">
                    {g.kind === 'whitepaper' ? <FileCode2 size={14} className="text-info shrink-0" /> : <Film size={14} className="text-bronze shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="font-body text-sm text-cream truncate">
                        {g.kind === 'whitepaper' ? 'Technical Whitepaper' : 'Marketing Deck'}
                      </div>
                      <div className="font-mono text-[10px] text-cream/45">
                        {formatDateLong(g.created_at)} · {formatRelativeDate(g.created_at)}
                      </div>
                    </div>
                    <Badge tone={g.kind === 'whitepaper' ? 'info' : 'bronze'} className="!py-0 shrink-0">{g.kind.replace('_', ' ')}</Badge>
                    <ExternalLink size={11} className="text-bronze shrink-0" />
                  </a>
                ))}
              </div>
            </Section>
          )}

          {(tab === 'all' || tab === 'audits') && auditCycles.length > 0 && (
            <Section title="Audit cycles" subtitle="Each cycle generated a batch of suggestions. Track which ones got shipped.">
              <div className="space-y-2">
                {auditCycles.map(c => (
                  <Link key={c.iteration} to="/value"
                    className="flex items-center gap-3 px-3 py-2 rounded border border-cream/8 bg-navy/30 hover:bg-navy/60 hover:border-gold/30 transition-colors">
                    <TrendingUp size={14} className="text-bronze shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-body text-sm text-cream">
                        Iteration <span className="text-gold-soft">{c.iteration}</span> · {c.total} suggestions
                      </div>
                      <div className="font-mono text-[10px] text-cream/45">
                        {formatDateLong(c.generated_at)} · {c.high} HIGH-impact · {c.done}/{c.total} shipped
                      </div>
                    </div>
                    <div className="h-1.5 w-20 rounded-full bg-cream/8 overflow-hidden shrink-0">
                      <div className="h-full bg-success" style={{ width: `${c.total ? (c.done / c.total) * 100 : 0}%` }} />
                    </div>
                  </Link>
                ))}
              </div>
            </Section>
          )}

          {(tab === 'all' || tab === 'synergies') && h.synergies.length > 0 && (
            <Section title="Cross-portfolio synergies" subtitle="Strategic moves that cross app boundaries — merge, bundle, reuse, cross-sell.">
              <div className="space-y-2">
                {h.synergies.map(s => (
                  <div key={s.id} className="px-3 py-2.5 rounded border border-bronze/30 bg-bronze/5">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Network size={12} className="text-bronze shrink-0" />
                      <Badge tone="bronze" className="!py-0 uppercase">{s.type.replace('_', ' ')}</Badge>
                      <span className="font-mono text-[10px] text-cream/55">→</span>
                      <Link to="/apps/$slug" params={{ slug: s.to_slug }} className="font-mono text-[10px] text-gold-soft hover:underline">
                        {s.to_slug}
                      </Link>
                      <span className="font-mono text-[10px] text-cream/40 ml-auto">iter {s.generated_iteration} · {formatRelativeDate(s.generated_at)}</span>
                    </div>
                    <p className="font-body text-sm text-cream/80 leading-relaxed">{s.rationale}</p>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}
    </Card>
  )
}

function TabBtn({ label, active, on }: { label: string; active: boolean; on: () => void }) {
  return (
    <button
      onClick={on}
      className={cn(
        'px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-[0.1em] transition-colors',
        active ? 'bg-gold/15 text-gold-soft' : 'text-cream/55 hover:text-cream',
      )}
    >
      {label}
    </button>
  )
}

function Section({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div>
        <h4 className="font-heading text-base text-cream">{title}</h4>
        <p className="font-body text-xs text-cream/55">{subtitle}</p>
      </div>
      {children}
    </div>
  )
}

function ValuationRow({ v, prev }: { v: ValuationHistoryEntry; prev?: ValuationHistoryEntry }) {
  const cur = v.acquisition?.midpoint
  const prevMid = prev?.acquisition?.midpoint
  const delta = (typeof cur === 'number' && typeof prevMid === 'number') ? cur - prevMid : null
  const pct = (delta != null && prevMid) ? Math.round((delta / prevMid) * 100) : null
  const up = delta != null && delta > 0
  const down = delta != null && delta < 0

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded border border-cream/8 bg-navy/30">
      <div className="size-8 rounded-md bg-gold/10 flex items-center justify-center shrink-0">
        <TrendingUp size={14} className="text-gold-soft" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-display italic text-2xl text-cream font-light leading-none">
            {formatCurrency(cur, { compact: true })}
          </span>
          {delta != null && (
            <span className={cn('font-mono text-[10px] uppercase tracking-[0.1em] flex items-center gap-1',
              up ? 'text-success' : down ? 'text-danger' : 'text-cream/55')}>
              {up && <ArrowUpRight size={11} />}
              {down && <ArrowDownRight size={11} />}
              {delta > 0 && '+'}
              {formatCurrency(delta, { compact: true })}
              {pct != null && ` (${pct > 0 ? '+' : ''}${pct}%)`}
            </span>
          )}
        </div>
        <div className="font-mono text-[10px] text-cream/45 mt-1">
          {formatDateLong(v.generated_at)} · {formatRelativeDate(v.generated_at)} · {v.model}
        </div>
        {v.acquisition?.rationale && (
          <div className="font-body text-xs text-cream/65 mt-1.5 line-clamp-2">{v.acquisition.rationale}</div>
        )}
      </div>
    </div>
  )
}
